import { pipSize, pointSize } from '../trading/levels';
import type { Anchor } from './model';

/** What the measure tool reports between two points on the chart. */
export interface Measurement {
  up: boolean;
  lines: string[];
}

function duration(seconds: number): string {
  const s = Math.abs(Math.round(seconds));
  const d = Math.floor(s / 86_400);
  const h = Math.floor((s % 86_400) / 3_600);
  const m = Math.floor((s % 3_600) / 60);
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
}

/**
 * The change from `a` to `b`: price difference, points, pips and percent, then the number of bars and the time between.
 * `bars` is the distance in bars (logical positions), which skips weekends like the chart does.
 */
export function measure(a: Anchor, b: Anchor, digits: number, bars: number): Measurement {
  const diff = b.p - a.p;
  const sign = diff > 0 ? '+' : diff < 0 ? '−' : '';
  const abs = Math.abs(diff);
  const points = Math.round(abs / pointSize(digits));
  const pips = abs / pipSize(digits);
  const pct = a.p !== 0 ? (abs / a.p) * 100 : 0;
  const count = Math.round(Math.abs(bars));
  return {
    up: diff >= 0,
    lines: [
      `${sign}${abs.toFixed(digits)} (${sign}${pct.toFixed(2)}%)`,
      `${points} points · ${Number(pips.toFixed(1))} pips`,
      `${count} bar${count === 1 ? '' : 's'} · ${duration(b.t - a.t)}`,
    ],
  };
}

export interface Ohlc {
  o: number;
  h: number;
  l: number;
  c: number;
}

/**
 * The open, high, low or close of `bar` nearest to the pointer, when it is within `maxPixels` of it (the magnet);
 * otherwise null and the pointer's own price is used.
 */
export function magnetPrice(
  bar: Ohlc,
  pointerY: number,
  yOf: (price: number) => number | null,
  maxPixels = 14,
): number | null {
  let best: { price: number; distance: number } | null = null;
  for (const price of [bar.o, bar.h, bar.l, bar.c]) {
    const y = yOf(price);
    if (y === null) continue;
    const distance = Math.abs(y - pointerY);
    if (distance <= maxPixels && (!best || distance < best.distance)) best = { price, distance };
  }
  return best?.price ?? null;
}

/** The bar that starts at or just before `time` (bars sorted by time); null before the first one. */
export function barAt<T extends { t: number }>(bars: readonly T[], time: number): T | null {
  let lo = 0;
  let hi = bars.length - 1;
  let found: T | null = null;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (bars[mid]!.t <= time) {
      found = bars[mid]!;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}
